/**
 * Demo-only data. Replace with PN mapping API when backend source
 * processing is implemented.
 *
 * Nothing in this module is derived from real parsing, subject-matching,
 * or CIOMS extraction - it is a deterministic mock dataset used purely to
 * make the Data Mapping page demoable. `DataMapping.js` is the only
 * consumer; when a real `GET/POST /patient-narratives/{id}/data-mapping`
 * API exists, this module (and the `handleAutoMap` call site that reads
 * it) is the boundary to swap out.
 */

// Options offered by the "Select CIOMS case..." dropdown for patients
// without a matched case yet, plus the pool "+ Add" pulls unused entries
// from. Metadata (event count / follow-up label) is deterministic so
// picking the same case always shows the same demo details.
export const CIOMS_CASE_OPTIONS = [
  "2025FAB000412",
  "2025FAB000598",
  "2025FAB000731",
  "2025FAB000845",
];

export const CIOMS_CASE_METADATA = {
  "2025FAB000412": { eventCount: 2, followUpLabel: null },
  "2025FAB000598": { eventCount: 2, followUpLabel: "F1" },
  "2025FAB000731": { eventCount: 1, followUpLabel: null },
  "2025FAB000845": { eventCount: 1, followUpLabel: "F1" },
};

// Available options shown in each source-listing dropdown before Auto-Map
// runs, so the selectors feel populated even pre-map (per-category, a
// couple of plausible alternatives plus the one Auto-Map ultimately picks).
export const SOURCE_LISTING_OPTIONS = {
  severity: [
    "Listing 16.2.7.1 Listing of Treatment-Emergent Adverse Events with Severity, Safety Set",
    "Listing 16.2.7.3 Listing of All Adverse Events, Safety Set",
  ],
  narrativeInterestCategory: [
    "Listing 16.2.7.2 Listing of TEAEs by Narrative Interest Category, Safety Set",
    "Listing 16.2.7.4 Listing of Adverse Events of Special Interest, Safety Set",
  ],
  exposure: [
    "Listing 16.2.5.1 Listing of Study Drug Exposure, Safety Set",
    "Listing 16.2.5.2 Listing of Dose Modifications, Safety Set",
  ],
  medicalHistory: ["Listing 16.2.3.1 Listing of Medical History, Safety Set"],
  concomitantMedications: [
    "Listing 16.2.4.1 Listing of Concomitant Medications, Safety Set",
  ],
};

// Available CIOMS source files shown before Auto-Map runs.
export const CIOMS_SOURCE_OPTIONS = [
  "FD-7931_CIOMS_II_Line_Listing_30JUN2025.pdf",
  "FD-7931_CIOMS_II_Line_Listing_15MAY2025.pdf",
];

// Mock label shown for the linked CSR's source-listings project, e.g.
// "from FD-7931 CSR project". Whether this text is shown at all is driven
// by the PN's real `linked_csr_id`; the label text itself stays mock.
export const DEMO_LINKED_CSR_LABEL = "FD-7931";

export const PN_DATA_MAPPING_DEMO = {
  sourceListings: {
    severity:
      "Listing 16.2.7.1 Listing of Treatment-Emergent Adverse Events with Severity, Safety Set",
    narrativeInterestCategory:
      "Listing 16.2.7.2 Listing of TEAEs by Narrative Interest Category, Safety Set",
    exposure: "Listing 16.2.5.1 Listing of Study Drug Exposure, Safety Set",
    medicalHistory: null,
    concomitantMedications: null,
  },
  cioms: {
    filename: "FD-7931_CIOMS_II_Line_Listing_30JUN2025.pdf",
    caseCount: 3,
    subjectCount: 2,
    identifierField: "Patient Subject #",
    followUpPolicy: "follow-ups supersede initial reports",
  },
  // Per-patient mapping rows. `ciomsCases` is empty for patients Auto-Map
  // could not match to a CIOMS case - the page's own "Select CIOMS case..."
  // / "+ Add" interactions are what populate it from here on, locally.
  mappings: [
    {
      patientId: "7931-0105-008",
      reasons: ["SAE"],
      listingEvents: 2,
      ciomsCases: [
        { caseId: "2025FAB000412", eventCount: 2, followUpLabel: null },
      ],
      status: "matched",
    },
    {
      patientId: "7931-0203-014",
      reasons: ["Death", "SAE"],
      listingEvents: 1,
      ciomsCases: [
        { caseId: "2025FAB000598", eventCount: 2, followUpLabel: "F1" },
      ],
      status: "matched",
    },
    {
      patientId: "7931-0203-019",
      reasons: ["SAE", "Discontinuation"],
      listingEvents: 1,
      ciomsCases: [],
      status: "unmatched",
    },
    {
      patientId: "7931-0108-002",
      reasons: ["SAE"],
      listingEvents: 1,
      ciomsCases: [],
      status: "unmatched",
    },
    {
      patientId: "7931-0301-006",
      reasons: ["Discontinuation"],
      listingEvents: 1,
      ciomsCases: [],
      status: "unmatched",
    },
  ],
};
